import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useBackGestureHandler } from './hooks/useBackGestureHandler';
import { BusinessProvider } from './shared/contexts/BusinessContext';
import { SectorProvider } from './contexts/SectorContext';
import { AuthBusinessProvider, useAuthBusiness } from './contexts/AuthBusinessContext';
import { RealtimeDataProvider } from './contexts/RealtimeDataContext';
import { ModuleAccessProvider } from './contexts/ModuleAccessContext';
import { ToastProvider } from './contexts/ToastContext';
import { detectBrandFromDomain } from './utils/domainDetection';
import BusinessNavigationGuard from './components/common/BusinessNavigationGuard';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { isAdminUser } from './utils/userUtils';
import { UnauthorizedPage, getRoleDefaultPath } from './components/common/ProtectedRoute';
import NetworkStatusBanner from './components/common/NetworkStatusBanner';
import RouteHistoryTracker, { getLastVisitedRoute } from './components/common/RouteHistoryTracker';
import initKeyboardScrollFix from './utils/keyboardScrollFix';

// Public Pages - Lazy Loaded to reduce main bundle size for QR menu users
const Login = React.lazy(() => import('./pages/Login'));
const SectorGoLogin = React.lazy(() => import('./sectors/food-beverage/pages/operations/SectorGoLogin'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const Register = React.lazy(() => import('./pages/Register'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// 🚀 LAZY LOADING - Memory'yi %70 azaltır
const QROrderScreen = React.lazy(() => import('./sectors/food-beverage/pages/qr/QROrderScreen'));
const OrderTracking = React.lazy(() => import('./sectors/food-beverage/pages/qr/OrderTracking'));
const PublicBlogListPage = React.lazy(() => import('./sectors/food-beverage/pages/qr/PublicBlogListPage.jsx'));
const PublicBlogDetailPage = React.lazy(() => import('./sectors/food-beverage/pages/qr/PublicBlogDetailPage.jsx'));

// 🏨 Hospitality (Otelcilik) Public Routes
const HotelBookingScreen = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelBookingScreen'));
const HotelRoomList = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelRoomList'));
const HotelRoomDetail = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelRoomDetail'));
const HotelAboutPage = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelAboutPage'));
const HotelGalleryPage = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelGalleryPage'));
const HotelContactPage = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelContactPage'));
const HotelBookPage = React.lazy(() => import('./sectors/service/pages/hospitality/booking/HotelBookPage'));
const BookingConfirmation = React.lazy(() => import('./sectors/service/pages/hospitality/booking/BookingConfirmation'));

const AdminModule = React.lazy(() => import('./modules/AdminModule'));
const FoodBeverageModule = React.lazy(() => import('./modules/FoodBeverageModule'));
const RetailModule = React.lazy(() => import('./modules/RetailModule'));
const ServiceModule = React.lazy(() => import('./modules/ServiceModule'));
const BusinessModule = React.lazy(() => import('./modules/BusinessModule'));
// const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage')); // kullanılmıyor
const PackageDetail = React.lazy(() => import('./pages/PackageDetail'));
const SubAgentManager = React.lazy(() => import('./components/SubAgentManager'));
const SocialMediaManagement = React.lazy(() => import('./pages/social-media/SocialMediaManagement'));
const SocialMediaPostForm = React.lazy(() => import('./pages/social-media/SocialMediaPostForm'));
const AIPostWizard = React.lazy(() => import('./pages/social-media/AIPostWizard'));
const DesignStudio = React.lazy(() => import('./pages/DesignStudio'));
const PostSizeSettings = React.lazy(() => import('./pages/PostSizeSettings'));
const ImageEditor = React.lazy(() => import('./pages/ImageEditor_new'));
// const CampaignManagement = React.lazy(() => import('./sectors/food-beverage/pages/campaigns/CampaignManagement')); // kullanılmıyor
const WhatsAppBotManagement = React.lazy(() => import('./shared/components/WhatsAppBotManagement'));
const BusinessWhatsappIntegration = React.lazy(() => import('./pages/business/whatsapp/integration'));
const WhatsAppQuickRepliesPage = React.lazy(() => import('./pages/business/whatsapp/quickReplies'));
const InstagramAIDashboard = React.lazy(() => import('./pages/instagram-ai/InstagramAIDashboard'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));
const AdvancedSecuritySettings = React.lazy(() => import('./pages/AdvancedSecuritySettings'));
const BusinessSupportCenter = React.lazy(() => import('./pages/business/BusinessSupportCenter'));
const NotificationCenter = React.lazy(() => import('./pages/business/NotificationCenter'));
const EmployeePortal = React.lazy(() => import('./pages/portal/EmployeePortal'));
const IntegrationCenter = React.lazy(() => import('./pages/IntegrationCenter'));
const GlobalOrderOverlay = React.lazy(() => import('./components/GlobalOrderOverlay'));
const DomainManagement = React.lazy(() => import('./pages/settings/DomainManagement'));
const PaymentResult = React.lazy(() => import('./pages/PaymentResult'));
const WholesaleRedirect = React.lazy(() => import('./components/auth/WholesaleRedirect'));
const ProductionRedirect = React.lazy(() => import('./components/auth/ProductionRedirect'));
const GlobalNotificationHandler = React.lazy(() => import('./components/notifications/GlobalNotificationHandler'));
const PWAUpdateNotification = React.lazy(() => import('./components/common/PWAUpdateNotification'));


// Redirect Handler for Legacy/Short Links
const RedirectHandler = ({ targetBase }) => {
  const location = useLocation();
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');

  const parts = location.pathname.split('/');
  const rest = parts.slice(2).join('/');
  
  let finalTargetBase = targetBase;
  
  // 🏨 [HOSPITALITY REDIRECT PATCH]
  // If target is restaurant settings but user is hospitality, redirect to hospitality settings
  if (targetBase === '/sectors/restaurant/settings' && selectedBusiness) {
    const isHospitality = (selectedBusiness.sector === 'hospitality' || 
                          selectedBusiness.category === 'hospitality' || 
                          (selectedBusiness.name || '').toLowerCase().includes('otel'));
    if (isHospitality) {
      finalTargetBase = '/sectors/service/hospitality/settings';
    }
  }

  const target = rest ? `${finalTargetBase}/${rest}` : finalTargetBase;
  return <Navigate to={`${target}${location.search}`} replace />;
};

// Loading Component - Sade, Titremesiz, Nötr ve Pürüzsüz Yükleme Ekranı
const LoadingPage = ({ module }) => (
  <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center p-6 space-y-4">
    <div className="w-10 h-10 border-3 border-gray-100 border-t-black rounded-full animate-spin"></div>
    {module && (
      <p className="font-sans text-[10px] font-black uppercase tracking-[0.25em] text-gray-400 select-none">
        {module}
      </p>
    )}
  </div>
);

/**
 * HomeElement
 * Conditionally renders LandingPage or QR Menu / Hotel Booking based on domain
 */
const HomeElement = () => {
  const isSystemHost = React.useMemo(() => {
    const h = window.location.hostname.toLowerCase();
    return h.includes('sectorpilot') || h.includes('prmanager') || h === 'sectoras.com' || h === 'www.sectoras.com';
  }, []);

  const [brandedInfo, setBrandedInfo] = React.useState(null);
  const [domainLoading, setDomainLoading] = React.useState(!isSystemHost);
  const [appId, setAppId] = useState(null);
  const { isAuthenticated, currentUserRole, memberships, currentUser, isLoading: authLoading } = useAuthBusiness();

  // 📱 EĞER NATIVE MOBIL İÇİNDEYSEK:
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App: CapApp }) => {
        CapApp.getInfo().then(info => {
          setAppId(info.id || '');
        }).catch(() => setAppId(''));
      }).catch(() => setAppId(''));
    }
  }, []);

  React.useEffect(() => {
    const checkDomain = async () => {
      const hostname = window.location.hostname;
      console.log('🌐 Domain check started for:', hostname);

      try {
        const detection = await detectBrandFromDomain(hostname);
        console.log('🔍 Domain detection result:', detection);

        if (detection) {
          setBrandedInfo(detection);
          // 🚀 SEO TAG INJECTION
          try {
            const { db } = await import('./services/firebase');
            const { doc, getDoc } = await import('firebase/firestore');
            const sector = detection.sector || 'food_beverage';
            
            console.log('📡 Fetching SEO/Brand data for:', detection.businessId, 'Sector:', sector);

            let sRef, bizRef;
            if (sector === 'service') {
              sRef = doc(db, 'businesses', 'service', 'businesses', detection.businessId, 'settings', 'hotel_info');
              bizRef = doc(db, 'businesses', 'service', 'businesses', detection.businessId, 'settings', 'business_info');
            } else {
              sRef = doc(db, 'businesses', sector, 'businesses', detection.businessId, 'menu', 'qr_settings');
              bizRef = doc(db, 'businesses', sector, 'businesses', detection.businessId, 'settings', 'business_info');
            }

            const [sSnap, bizSnap] = await Promise.all([getDoc(sRef), getDoc(bizRef)]);
            const bizData = bizSnap.exists() ? bizSnap.data() : {};

            if (sSnap.exists()) {
              const data = sSnap.data();
              let finalTitle, finalDescription, themeColor, logo;

              if (sector === 'service') {
                finalTitle = data.hotelName || bizData.businessName || bizData.name || 'Otel Rezervasyon';
                finalDescription = data.description || bizData.aboutUs || `${finalTitle} Online Rezervasyon Sistemi`;
                themeColor = data.primaryColor || bizData.themeColor;
                logo = data.logo || bizData.logo;
              } else {
                const brandSeo = (data[detection.brandSlug]?.seo || data['all']?.seo || {});
                const brandConfig = (data[detection.brandSlug] || data['all'] || {});
                
                const isSubBrand = detection.brandSlug && detection.brandSlug !== 'default' && detection.brandSlug !== 'all';
                let brandFallbackName = 'Dijital Menü';
                if (isSubBrand) {
                  const bSlugLower = detection.brandSlug.toLowerCase();
                  if (bSlugLower.includes('firuz')) brandFallbackName = 'Firuzağa';
                  else if (bSlugLower.includes('sirez')) brandFallbackName = 'Şirezar';
                  else brandFallbackName = detection.brandSlug.charAt(0).toUpperCase() + detection.brandSlug.slice(1);
                } else {
                  brandFallbackName = data.restaurantName || bizData.businessName || bizData.name || 'Madem';
                }

                finalTitle = brandSeo.metaTitle || brandConfig.restaurantName || brandFallbackName;
                finalDescription = brandSeo.metaDescription || brandConfig.description || `${finalTitle} Dijital Menü ve Sipariş Sistemi`;
                themeColor = brandConfig.primaryColor || data.primaryColor || bizData.themeColor;
                logo = brandSeo.favicon || brandConfig.logo || (isSubBrand ? '' : (data.logo || bizData.logo));
              }

              const { setMetaTags } = await import('./utils/seoHelper');
              setMetaTags({
                title: finalTitle,
                description: finalDescription,
                favicon: logo,
                image: logo
              });

              // Pre-hydration önbelleğini tazele
              try {
                localStorage.setItem('qr_cached_seo_' + hostname.toLowerCase(), JSON.stringify({
                  title: finalTitle,
                  favicon: logo
                }));
              } catch(e) {}

              const { injectDynamicManifest } = await import('./utils/pwaHelper');
              injectDynamicManifest(bizData, {
                restaurantName: finalTitle,
                logo: logo,
                metaDescription: finalDescription,
                themeColor: themeColor
              });
            }
          } catch (e) {
            console.error('SEO injection error:', e);
          }
        } else {
          // Localhost veya system domain kontrolü
          const urlParams = new URLSearchParams(window.location.search);
          const hasBusinessInfo = window.location.pathname.startsWith('/menu/') || 
                                  window.location.pathname.startsWith('/qr/menu/') || 
                                  window.location.pathname.startsWith('/hotel/') ||
                                  urlParams.get('businessId');
          
          if (hasBusinessInfo) {
            console.log('ℹ️ No branded domain detected, but URL contains business info.');
          }
        }
      } catch (err) {
        console.error('❌ Critical error in domain detection:', err);
      } finally {
        console.log('✅ Domain check finished.');
        setDomainLoading(false);
      }
    };
    checkDomain();
  }, []);

  // ✅ KESİN ÇÖZÜM: Özel domain (brandedInfo) tespit edildiyse authLoading'i BEKLEME!
  // Gizli sekmede Firebase Auth 10+ saniye yavaş init olabilir.
  // QR menüye gelen ziyaretçi zaten giriş yapmamış, auth'u beklemeye gerek yok.
  if (domainLoading) {
    return <LoadingPage />;
  }

  // Domain yüklemesi bitti ve özel domain tespit edildi → Hemen göster
  if (brandedInfo) {
    const currentPath = window.location.pathname.toLowerCase();
    const isBlogDetail = currentPath.includes('/blog/') && currentPath.split('/blog/')[1]?.trim().length > 0;
    const isBlogList = currentPath.includes('/blog');

    if (isBlogDetail) {
      return (
        <Suspense fallback={<LoadingPage module="Blog Detay" />}>
          <PublicBlogDetailPage brandedInfo={brandedInfo} />
        </Suspense>
      );
    }

    if (isBlogList) {
      return (
        <Suspense fallback={<LoadingPage module="Blog" />}>
          <PublicBlogListPage brandedInfo={brandedInfo} />
        </Suspense>
      );
    }

    if (brandedInfo.sector === 'service') {
      return (
        <Suspense fallback={<LoadingPage />}>
          <HotelBookingScreen 
            businessSlug={brandedInfo.businessSlug} 
            brandSlug={brandedInfo.brandSlug} 
            businessId={brandedInfo.businessId}
            brandedInfo={brandedInfo}
          />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<LoadingPage />}>
        <QROrderScreen 
          businessSlug={brandedInfo.businessSlug} 
          brandSlug={brandedInfo.brandSlug} 
          businessId={brandedInfo.businessId}
          brandedInfo={brandedInfo}
        />
      </Suspense>
    );
  }


  // 🛵 SECTORGO KURYEDE OTURUM & NATIVE AÇILIŞ KONTROLÜ
  const courierSession = (() => {
    try {
      const raw = localStorage.getItem('sectorgo_courier_session');
      return raw ? JSON.parse(raw) : null;
    } catch(e) { return null; }
  })();

  if (courierSession && courierSession.phone) {
    const primaryBiz = courierSession.activeBusinessId || (courierSession.businesses?.[0]?.businessId);
    if (primaryBiz) {
      return <Navigate to={`/sectors/food-beverage/courier-app?businessId=${primaryBiz}`} replace />;
    }
    return <Navigate to="/sectorgo/login" replace />;
  }

  if (Capacitor.isNativePlatform()) {
    // SectorGo kurye uygulaması ise kurye girişine yönlendir
    if (appId === 'com.sectorgo.courier') {
      return <Navigate to="/sectorgo/login" replace />;
    }
    // Eğer appId 'com.sectorpilot.app' veya 'com.prmanager.agent' ise normal akış devam eder (işletme girişi)
  }

  // 🚀 AUTH REDIRECT: Giriş yapmış kullanıcıyı landing yerine dashboard'a gönder
  const isAdmin = isAdminUser(currentUser);

  // 🛡️ SECURITY & UX FIX: Eğer özel bir domain (QR Menü/Otel) tespit edildiyse, 
  // kullanıcı login olsa bile onu dashboard'a ATMA. QR menü içeriğini görmeye devam etsin.
  if (isAuthenticated && !brandedInfo) {
    // 🛡️ SECURITY FIX: Public pages (hotel, menu, etc.) should allow authenticated users to stay
    const path = window.location.pathname;
    if (path.startsWith('/hotel/') || path.startsWith('/menu/') || path.startsWith('/qr/menu/')) {
       // On main domains without custom domain logic, let the router handle these paths
       // If we reached here, it means brandedInfo was null (likely localhost or main domain)
       // We should return null to allow the <Routes> in App to match the specific /hotel/ routes.
       return null; 
    }

    // A. Admin Redirect (Highest Priority)
    if (isAdmin) {
      console.log('🎯 Landing: Admin detected, redirecting to admin dashboard');
      return <Navigate to="/dashboard/admin/" replace />;
    }

    // 🛡️ SMART RESUME: PWA veya Android alta atılıp açıldığında kaldığı ekrandan devam et
    const storedBizId = localStorage.getItem('selectedBusinessId');
    const lastVisitedRoute = getLastVisitedRoute(storedBizId);
    if (lastVisitedRoute && !lastVisitedRoute.includes('/login')) {
      console.log('🔄 Smart Resume: Kaldığı ekrandan devam ediliyor:', lastVisitedRoute);
      return <Navigate to={lastVisitedRoute} replace />;
    }

    // B. SMART REDIRECT: Tek işletmesi olan STANDART kullanıcıları direkt sektöre gönder
    // Not: Sahipler (owner) için de tek işletme durumunda kolaylık olması için otomatik yönlendirme aktifleştirildi.
    // eslint-disable-next-line no-unused-vars
    const isOwner = currentUserRole === 'business_owner' || currentUserRole === 'owner';
    if (memberships?.length === 1 && currentUserRole !== 'cari') {
      const m = memberships[0];
      
      const rawSector = (m.sector || m.sector_id || '').toLowerCase();
      const categoryName = (m.category_name || m.category || '').toLowerCase();
      
      const isHospitality = categoryName.includes('otel') || 
                           categoryName.includes('konaklama') || 
                           categoryName.includes('hospitality') ||
                           categoryName.includes('turizm') ||
                           categoryName.includes('seyahat') ||
                           rawSector === 'hospitality' ||
                           rawSector === 'hotel' ||
                           rawSector === '7hxb9mxwp8e1dtp2suvi';

      let targetPath = '/sectors/food-beverage/dashboard';
      
      if (isHospitality) {
        targetPath = '/sectors/service/hospitality/dashboard';
      } else if (rawSector === 'retail' || rawSector === 'gaghwmsccfa3wfoqyhdtp') {
        targetPath = '/sectors/retail/dashboard';
      } else if (rawSector === 'service' || rawSector === '7hxb9mxwp8e1dtp2suvi') {
        targetPath = '/sectors/service/dashboard';
      }
      
      console.log(`🚀 Landing: SMART-navigating to ${rawSector} dashboard:`, targetPath);
      return <Navigate to={targetPath} replace />;
    }

    const defaultPath = getRoleDefaultPath(currentUserRole || (isAdmin ? 'admin' : 'owner'));
    return <Navigate to={defaultPath} replace />;
  }
  
  return <LandingPage />;
};

/**
 * BrandedRouteWrapper
 * Özel domain'de /blog, /kategori, /kategori/urun gibi
 * doğrudan linklerde brandedInfo prop'unu doğru bileşene iletir.
 * LocalStorage cache ile çalışır — ekstra gecikme yoktur.
 */
const BrandedRouteWrapper = ({ normalElement }) => {
  const location = useLocation();
  const hostname = window.location.hostname;
  const isDev = hostname.includes('localhost') || hostname === '127.0.0.1';

  const [state, setState] = React.useState(() => {
    // Geliştirme ortamında ve root path'te özel domain yoktur
    if (isDev && location.pathname === '/' && !window.location.search.includes('brand')) {
      return { brandedInfo: null, loading: false };
    }
    // localStorage cache'ten oku (1 saat geçerli - Sadece geçerli businessId!)
    const cacheKey = `brand_domain_cache_${hostname.toLowerCase()}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { result, timestamp } = JSON.parse(cached);
        if (result && result.businessId && (Date.now() - timestamp < 3600000)) {
          return { brandedInfo: result, loading: false };
        } else {
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (e) { /* ignore */ }
    return { brandedInfo: null, loading: true };
  });

  React.useEffect(() => {
    if (!state.loading) return;
    detectBrandFromDomain(hostname)
      .then(result => {
        // Cache'e sadece geçerli sonucu yaz
        if (result && result.businessId) {
          try {
            const cacheKey = `brand_domain_cache_${hostname.toLowerCase()}`;
            localStorage.setItem(cacheKey, JSON.stringify({ result, timestamp: Date.now() }));
          } catch(e) {}
        }
        setState({ brandedInfo: result, loading: false });
      })
      .catch(err => {
        console.error('❌ [BrandedRouteWrapper] Tespit hatası:', err);
        setState({ brandedInfo: null, loading: false });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading]);

  // Yükleniyor — LoadingPage göster, beyaz ekran yok
  if (state.loading) {
    return <LoadingPage module="Menü Yükleniyor" />;
  }

  const brandedInfo = state.brandedInfo;
  // useLocation ile React Router reaktif path
  const rawPath = location.pathname.toLowerCase().replace(/^\/|\/$/g, '');
  const pathParts = rawPath.split('/').filter(Boolean);

  const isBlogDetail = pathParts.length >= 2 && pathParts[0] === 'blog';
  const isBlogList   = pathParts.length === 1 && pathParts[0] === 'blog';

  if (isBlogDetail) {
    return (
      <Suspense fallback={<LoadingPage module="Blog Detay" />}>
        <PublicBlogDetailPage brandedInfo={brandedInfo} />
      </Suspense>
    );
  }

  if (isBlogList) {
    return (
      <Suspense fallback={<LoadingPage module="Blog" />}>
        <PublicBlogListPage brandedInfo={brandedInfo} />
      </Suspense>
    );
  }

  // Kategori & ürün sayfaları
  const categoryId = pathParts.length >= 1 && pathParts[0] !== 'blog' ? pathParts[0] : null;
  const productId  = pathParts.length >= 2 && pathParts[0] !== 'blog' ? pathParts[1] : null;

  // Özel domain değil (null brandedInfo) → normalElement göster ama URL parametrelerini ilet
  if (!state.brandedInfo) {
    return (
      <Suspense fallback={<LoadingPage module="Menü" />}>
        <QROrderScreen
          categoryId={categoryId}
          productId={productId}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingPage module="Menü" />}>
      <QROrderScreen
        businessSlug={brandedInfo.businessSlug}
        brandSlug={brandedInfo.brandSlug}
        businessId={brandedInfo.businessId}
        brandedInfo={brandedInfo}
        categoryId={categoryId}
        productId={productId}
      />
    </Suspense>
  );
};

const BackGestureWrapper = ({ children }) => {
  useBackGestureHandler();
  return children;
};

// Guard: Auth yüklenirken boş sayfa veya loading göster (Loops'u engellemek için)
const RootAppGuard = ({ children }) => {
  const { isLoading } = useAuthBusiness();
  
  // 🚀 QR MENÜ BY-PASS: Eğer istek özel domainden (branded domain) veya QR menü rotasından geliyorsa, 
  // Firebase Auth başlangıç kimlik doğrulama yüklemesini beklemeyip direkt sayfayı göster.
  const hostname = window.location.hostname;
  const isBrandedDomain = !hostname.includes('prmanager') && !hostname.includes('localhost') && hostname.includes('.');
  const isQRMenuRoute = window.location.pathname.startsWith('/menu/') || 
                         window.location.pathname.startsWith('/qr/menu/') || 
                         window.location.pathname.startsWith('/hotel/') ||
                         window.location.pathname === '/'; // Ana landing veya custom domain ana rotası
  
  if (isLoading && !isBrandedDomain && !isQRMenuRoute) {
    return <LoadingPage module="Kimlik Doğrulama" />;
  }
  return children;
};

// 🛡️ ARCHITECTURE SYNC: Auth'tan gelen verileri BusinessProvider'a köprüleyen bileşen
const BusinessSyncWrapper = ({ children }) => {
  const { userBusinesses, isLoading, refreshUserData } = useAuthBusiness();
  
  return (
    <BusinessProvider 
      saasBusinesses={userBusinesses} 
      saasLoading={isLoading}
      refreshAuth={refreshUserData}
    >
      {children}
    </BusinessProvider>
  );
};

function App() {
  const [globalError, setGlobalError] = useState(null);

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isCustomDomain = !hostname.includes('prmanager') && !hostname.includes('sectorpilot') && !hostname.includes('localhost') && hostname.includes('.');
  const isQRMenuRoute = pathname.startsWith('/menu/') || pathname.startsWith('/qr/menu/') || pathname.startsWith('/hotel/');
  const isPublicQR = isCustomDomain || isQRMenuRoute;

  useEffect(() => {
    const handleErrorEvent = (e) => {
      setGlobalError(e.detail);
    };
    window.addEventListener('system-error-triggered', handleErrorEvent);
    return () => {
      window.removeEventListener('system-error-triggered', handleErrorEvent);
    };
  }, []);

  useEffect(() => {
    const applyZoom = () => {
      const savedZoom = localStorage.getItem('appZoom');
      const savedIconScale = localStorage.getItem('hubIconScale');
      
      document.documentElement.style.zoom = savedZoom || '1';
      document.documentElement.style.setProperty('--hub-icon-scale', savedIconScale || '1');
    };
    applyZoom();
    window.addEventListener('storage', applyZoom);
    window.addEventListener('app-zoom-change', applyZoom);
    window.addEventListener('app-icon-scale-change', applyZoom);
    return () => {
      window.removeEventListener('storage', applyZoom);
      window.removeEventListener('app-zoom-change', applyZoom);
      window.removeEventListener('app-icon-scale-change', applyZoom);
    };
  }, []);

  // 📱 MOBIL PWA KLAVYE & EKRAN KAYMA RESETİ (Header kaybolmasını önler)
  useEffect(() => {
    const cleanup = initKeyboardScrollFix();
    return () => cleanup();
  }, []);

  return (
    <ErrorBoundary>
      <NetworkStatusBanner />
      <AuthBusinessProvider>
        <BusinessSyncWrapper>
          <RealtimeDataProvider>
            <SectorProvider>
              <ModuleAccessProvider>
                <ToastProvider>
                  <Router>
                    <RouteHistoryTracker />
                    <BackGestureWrapper>
                      <RootAppGuard>
                        <BusinessNavigationGuard>
                          {!isPublicQR && (
                            <Suspense fallback={null}>
                              <GlobalNotificationHandler />
                              <GlobalOrderOverlay />
                              <PWAUpdateNotification />
                            </Suspense>
                          )}
                          <Suspense fallback={<LoadingPage module="Ana Sistem" />}>
                            <Routes>
                              {/* Public Routes - No lazy loading needed */}
                              <Route path="/" element={<HomeElement />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/sectorgo/login" element={
                          <Suspense fallback={<LoadingPage module="SectorGo Kurye" />}>
                            <SectorGoLogin />
                          </Suspense>
                        } />
                        <Route path="/sectorgo" element={
                          <Suspense fallback={<LoadingPage module="SectorGo Kurye" />}>
                            <SectorGoLogin />
                          </Suspense>
                        } />
                        <Route path="/dashboard/admin/login" element={<AdminLogin />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/business/select" element={<BusinessModule />} />
                        <Route path="/:slug/toptan" element={<WholesaleRedirect />} />
                        <Route path="/:slug/uretim" element={<ProductionRedirect />} />

                        {/* 🔄 Short Link Redirects (Design Preview Helpers) */}
                        <Route path="/pos/*" element={<RedirectHandler targetBase="/sectors/restaurant/pos" />} />
                        <Route path="/finance/*" element={<RedirectHandler targetBase="/sectors/restaurant/finance" />} />
                        <Route path="/hr/*" element={<RedirectHandler targetBase="/sectors/restaurant/hr" />} />
                        <Route path="/kitchen/*" element={<RedirectHandler targetBase="/sectors/restaurant/kitchen" />} />
                        <Route path="/reports/*" element={<RedirectHandler targetBase="/sectors/restaurant/reports" />} />
                        <Route path="/inventory/*" element={<RedirectHandler targetBase="/sectors/restaurant/inventory" />} />
                        <Route path="/customers/*" element={<RedirectHandler targetBase="/sectors/restaurant/customers" />} />
                        <Route path="/settings/*" element={<RedirectHandler targetBase="/sectors/restaurant/settings" />} />

                        {/* Employee Portal - Public Route with Token */}
                        <Route
                          path="/portal/:token"
                          element={
                            <Suspense fallback={<LoadingPage module="Çalışan Portalı" />}>
                              <EmployeePortal />
                            </Suspense>
                          }
                        />

                        <Route
                          path="/qr/menu/:businessSlug"
                          element={
                            <Suspense fallback={<LoadingPage module="QR Menü" />}>
                              <QROrderScreen />
                            </Suspense>
                          }
                        />

                        {/* 📰 BLOG PUBLIC ROUTES - BrandedRouteWrapper özel domain'de brandedInfo iletir */}
                        <Route
                          path="/blog"
                          element={
                            <BrandedRouteWrapper
                              normalElement={
                                <Suspense fallback={<LoadingPage module="Blog" />}>
                                  <PublicBlogListPage />
                                </Suspense>
                              }
                            />
                          }
                        />
                        <Route
                          path="/blog/:slug"
                          element={
                            <BrandedRouteWrapper
                              normalElement={
                                <Suspense fallback={<LoadingPage module="Blog Detay" />}>
                                  <PublicBlogDetailPage />
                                </Suspense>
                              }
                            />
                          }
                        />
                        <Route
                          path="/menu/:bizSlug/:brandSlug/blog"
                          element={
                            <Suspense fallback={<LoadingPage module="Blog" />}>
                              <PublicBlogListPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/menu/:bizSlug/:brandSlug/blog/:slug"
                          element={
                            <Suspense fallback={<LoadingPage module="Blog Detay" />}>
                              <PublicBlogDetailPage />
                            </Suspense>
                          }
                        />

                        {/* QR Menu - Public Route (No Login Required) */}
                        <Route
                          path="/qr/menu"
                          element={
                            <Suspense fallback={<LoadingPage module="QR Menü" />}>
                              <QROrderScreen />
                            </Suspense>
                          }
                        />

                        {/* 🚀 QR MENU BİRLEŞTİRİLMİŞ ROTA (UNMOUNT ÖNLEYİCİ) */}
                        <Route
                          path="/menu/:businessSlug/:brandSlug/:categoryId?/:productId?"
                          element={
                            <Suspense fallback={<LoadingPage module="QR Menü" />}>
                              <QROrderScreen />
                            </Suspense>
                          }
                        />

                        {/* 🌐 ÖZEL DOMAIN KISA ROTALARI (Sadece özel alan adlarında kategori/ürün için) */}
                        <Route
                          path="/:categoryId/:productId?/*"
                          element={
                            <BrandedRouteWrapper
                              normalElement={
                                <Suspense fallback={<LoadingPage module="QR Menü" />}>
                                  <QROrderScreen />
                                </Suspense>
                              }
                            />
                          }
                        />

                        <Route
                          path="/track/:businessSlug"
                          element={
                            <Suspense fallback={<LoadingPage module="Sipariş Takibi" />}>
                              <OrderTracking />
                            </Suspense>
                          }
                        />

                        {/* 🏨 Hotel Booking - Public Route (No Login Required) */}
                        <Route
                          path="/hotel/:businessSlug"
                          element={
                            <Suspense fallback={<LoadingPage module="Otel Rezervasyon" />}>
                              <HotelBookingScreen />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/rooms"
                          element={
                            <Suspense fallback={<LoadingPage module="Odalar" />}>
                              <HotelRoomList />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/rooms/:roomSlug"
                          element={
                            <Suspense fallback={<LoadingPage module="Oda Detayı" />}>
                              <HotelRoomDetail />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/about"
                          element={
                            <Suspense fallback={<LoadingPage module="Hakkımızda" />}>
                              <HotelAboutPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/gallery"
                          element={
                            <Suspense fallback={<LoadingPage module="Galeri" />}>
                              <HotelGalleryPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/contact"
                          element={
                            <Suspense fallback={<LoadingPage module="İletişim" />}>
                              <HotelContactPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/book"
                          element={
                            <Suspense fallback={<LoadingPage module="Rezervasyon" />}>
                              <HotelBookPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/hotel/:businessSlug/booking/:reservationId"
                          element={
                            <Suspense fallback={<LoadingPage module="Rezervasyon Onayı" />}>
                              <BookingConfirmation />
                            </Suspense>
                          }
                        />

                        <Route
                          path="/payment/result"
                          element={
                            <Suspense fallback={<LoadingPage module="Ödeme Sonucu" />}>
                              <PaymentResult />
                            </Suspense>
                          }
                        />

                        {/* Package Detail Page - Lazy Loaded */}
                        <Route
                          path="/package/:id"
                          element={
                            <Suspense fallback={<LoadingPage module="Paket Detayı" />}>
                              <PackageDetail />
                            </Suspense>
                          }
                        />

                        {/* Sub Agent Manager */}
                        <Route
                          path="/sub-agents"
                          element={
                            <Suspense fallback={<LoadingPage module="Sub Agent Manager" />}>
                              <SubAgentManager />
                            </Suspense>
                          }
                        />

                        {/* Admin Module - Lazy Loaded */}
                        <Route
                          path="/dashboard/admin/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Admin Paneli" />}>
                              <AdminModule />
                            </Suspense>
                          }
                        />

                        {/* Diğer Modüller */}

                        {/* Business Management Module - Lazy Loaded */}
                        <Route
                          path="/brands/*"
                          element={
                            <Suspense fallback={<LoadingPage module="İşletme Yönetimi" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/subscription/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Abonelik Sistemi" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/dashboard/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Dashboard" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/business/*"
                          element={
                            <Suspense fallback={<LoadingPage module="İşletme Detayı" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/business-overview/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Genel Bakış" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/financial-status/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Mali Durum" />}>
                              <BusinessModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/business-support-center"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Destek Merkezi" />}>
                                <BusinessSupportCenter />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        {/* Social Media Module - Global */}
                        {/* Social Media Module - Wrapped in DashboardLayout */}
                        <Route
                          path="/social-media"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Sosyal Medya" />}>
                                <SocialMediaManagement />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/social-media/new"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Sosyal Medya" />}>
                                <SocialMediaPostForm />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/social-media/ai-post"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="AI Post Sihirbazı" />}>
                                <AIPostWizard />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/social-media/edit/:id"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Sosyal Medya" />}>
                                <SocialMediaPostForm />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/post-size-settings"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Boyut Rehberi" />}>
                                <PostSizeSettings />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/image-editor"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Fotoğraf Editörü" />}>
                                <ImageEditor />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route path="/design-studio" element={<DashboardLayout><Suspense fallback={<LoadingPage module="Tasarım Stüdyosu" />}><DesignStudio /></Suspense></DashboardLayout>} />

                        <Route
                          path="/instagram-ai"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Instagram AI" />}>
                                <InstagramAIDashboard />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        <Route
                          path="/business/whatsapp"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="WhatsApp Yönetimi" />}>
                                <WhatsAppBotManagement />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/business/whatsapp/quick-replies"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Hazır Mesaj Şablonları" />}>
                                <WhatsAppQuickRepliesPage />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/business/whatsapp/integration"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="WhatsApp Entegrasyonu" />}>
                                <BusinessWhatsappIntegration />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Profil Ayarları" />}>
                                <ProfileSettings />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/security"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Güvenlik Ayarları" />}>
                                <AdvancedSecuritySettings />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        {/* Notificaton Center */}
                        <Route
                          path="/notifications"
                          element={
                            <DashboardLayout>
                              <NotificationCenter />
                            </DashboardLayout>
                          }
                        />

                        {/* Sector Modules - Lazy Loaded */}
                        <Route
                          path="/sectors/food_beverage/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Yeme-İçme Sektörü" />}>
                              <FoodBeverageModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sectors/food-beverage/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Yeme-İçme Sektörü" />}>
                              <FoodBeverageModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sectors/restaurant/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Restoran Sistemi" />}>
                              <FoodBeverageModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sectors/retail/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Perakende Sektörü" />}>
                              <RetailModule />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/sectors/service/*"
                          element={
                            <Suspense fallback={<LoadingPage module="Hizmet Sektörü" />}>
                              <ServiceModule />
                            </Suspense>
                          }
                        />

                        {/* Trendyol Settings */}
                        <Route
                          path="/settings/integrations/trendyol"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Trendyol Ayarları" />}>
                                {React.createElement(React.lazy(() => import('./pages/settings/integrations/TrendyolSettings')))}
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        {/* Trendyol Virtual Brand Management */}
                        <Route
                          path="/settings/integrations/trendyol/brand/:brandId"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Sanal Marka Yönetimi" />}>
                                {React.createElement(React.lazy(() => import('./pages/settings/integrations/TrendyolBrandManagement')))}
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        {/* Integration Center */}
                        <Route
                          path="/integration-center"
                          element={
                            <DashboardLayout>
                              <IntegrationCenter />
                            </DashboardLayout>
                          }
                        />
                        <Route
                          path="/integrations"
                          element={
                            <DashboardLayout>
                              <IntegrationCenter />
                            </DashboardLayout>
                          }
                        />

                        {/* Domain Management */}
                        <Route
                          path="/settings/domains"
                          element={
                            <DashboardLayout>
                              <Suspense fallback={<LoadingPage module="Domain Yönetimi" />}>
                                <DomainManagement />
                              </Suspense>
                            </DashboardLayout>
                          }
                        />

                        {/* 404 Fallback Route */}
                        <Route
                          path="*"
                          element={
                            <Suspense fallback={<LoadingPage module="Sayfa Bulunamadı" />}>
                              <NotFoundPage />
                            </Suspense>
                          }
                        />
                      </Routes>
                    </Suspense>
                    </BusinessNavigationGuard>
                  </RootAppGuard>
                  </BackGestureWrapper>
                </Router>
              </ToastProvider>
            </ModuleAccessProvider>
          </SectorProvider>
        </RealtimeDataProvider>
      </BusinessSyncWrapper>
    </AuthBusinessProvider>
      {globalError && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 font-sans text-left">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-red-100 overflow-hidden transform animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-600 p-6 text-white flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-2xl">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-black tracking-tight text-lg">Sistem Çalışma Hatası</h3>
                <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-black uppercase tracking-wider">Hata Kodu: {globalError.errorCode}</span>
              </div>
            </div>
            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <h4 className="font-bold text-slate-800 text-sm">{globalError.title}</h4>
                <p className="text-xs text-slate-500 leading-relaxed font-semibold">{globalError.desc}</p>
              </div>
              <div className="bg-rose-50/50 border border-rose-100 p-3.5 rounded-2xl text-rose-950 text-xs font-semibold leading-relaxed">
                <span className="font-black text-rose-900 block mb-1">💡 Önerilen Çözüm Aksiyonu:</span>
                {globalError.action}
              </div>
              {globalError.rawDetails && (
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-xl max-h-28 overflow-y-auto">
                  <span className="text-[9px] font-black text-slate-450 block uppercase mb-1">Teknik Hata İzi (Stack Trace)</span>
                  <pre className="text-[9px] font-mono text-slate-500 whitespace-pre-wrap leading-relaxed select-all">
                    {globalError.rawDetails}
                  </pre>
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-100 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setGlobalError(null)}
                className="px-4 py-2 text-slate-500 hover:bg-slate-200 rounded-xl text-xs font-black border-0 cursor-pointer active:scale-95 transition-all"
              >
                Yoksay
              </button>
              <a
                href="/business-support-center"
                onClick={(e) => {
                  e.preventDefault();
                  setGlobalError(null);
                  window.location.href = '/business-support-center';
                }}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-black border-0 cursor-pointer active:scale-95 transition-all text-center no-underline shadow-md shadow-red-600/10"
              >
                Destek Merkezine Bildir
              </a>
            </div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
}

export default App;